// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useState } from "react";
import type { TourKind, TourStep } from "../dto/tourModels";
import { getTour } from "../registry/tourRegistry";
import { useTutorial } from "../context/TutorialContext";
import { useTutorialTour } from "../hooks/useTutorialTour";
import { TutorialCarousel } from "./TutorialCarousel";
import { navigateTo, useCurrentRoute } from "../../../router/navigation";
import { useTeamsEnabled } from "../../../context/FeatureFlagsContext";

const SKIP_GRACE_MS = 400;

function resolveStepTarget(step: TourStep): HTMLElement | null {
  const element = document.querySelector(step.target);
  return element instanceof HTMLElement ? element : null;
}

export function TutorialHost() {
  const { tour } = useTutorial();
  const { teams, settled } = useTeamsEnabled();

  if (!tour || !settled) {
    return null;
  }

  return <ActiveTour key={tour} kind={tour} teamsEnabled={teams} />;
}

function ActiveTour({ kind, teamsEnabled }: { kind: TourKind; teamsEnabled: boolean }) {
  const { finish, dismiss } = useTutorial();
  const route = useCurrentRoute();

  const definition = getTour(kind, teamsEnabled);
  const steps = definition.steps;
  const tour = useTutorialTour(steps);
  const currentStep = tour.currentStep;
  const isInDetail = tour.isInDetail;

  const activeStep = isInDetail ? tour.detail?.currentStep : currentStep;

  useEffect(() => {
    if (!activeStep?.routePath) {
      return undefined;
    }
    if (window.location.pathname !== activeStep.routePath) {
      navigateTo(activeStep.routePath);
    }
    return undefined;
  }, [kind, activeStep?.routePath]);

  const [target, setTarget] = useState<HTMLElement | null>(() =>
    activeStep ? resolveStepTarget(activeStep) : null,
  );
  useEffect(() => {
    setTarget(activeStep ? resolveStepTarget(activeStep) : null);
    const id = window.setInterval(() => {
      setTarget(activeStep ? resolveStepTarget(activeStep) : null);
    }, 250);
    return () => window.clearInterval(id);
  }, [activeStep, route.key]);

  const isNavigatingToStep =
    !!activeStep?.routePath &&
    window.location.pathname !== activeStep.routePath;

  const { advance, isLast } = tour;
  useEffect(() => {
    if (!currentStep || isLast || isInDetail) {
      return undefined;
    }
    if (currentStep.routePath) {
      return undefined;
    }
    if (target || isNavigatingToStep) {
      return undefined;
    }
    const id = window.setTimeout(() => advance(), SKIP_GRACE_MS);
    return () => window.clearTimeout(id);
  }, [currentStep, target, isNavigatingToStep, advance, isLast, isInDetail]);

  if (!currentStep) {
    return null;
  }

  return (
    <TutorialCarousel
      open
      target={target}
      tourTitle={definition.title}
      steps={steps}
      currentIndex={tour.currentIndex}
      onSelectIndex={tour.selectIndex}
      onFinish={() => finish(kind)}
      onDismiss={dismiss}
      detailOpen={isInDetail}
      detailSteps={tour.detail?.steps ?? []}
      detailIndex={tour.detail?.index ?? 0}
      onOpenDetail={tour.openDetail}
      onSelectDetailIndex={tour.selectDetailIndex}
      onFinishDetail={tour.finishDetail}
      onCloseDetail={tour.closeDetail}
    />
  );
}
